import { useToast } from "@chakra-ui/react";
import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from "@tanstack/react-query";
import { ISavedJobsPayload } from "_state/atoms/AppAtoms";
import { AxiosPromise, AxiosResponse } from "axios";
import {
  ErrorToastHandler,
  SuccessToastHandler,
} from "components/helpers/ToastHelpers";
import { axios } from "utils";
import { queryKeys } from "./queryKeys";
import { getSectorId } from "utils/jobs";

interface IObj {
  [key: string]: any;
}
export interface IGetJobsPayload extends IObj {
  search?: string;
  page: number;
  perPage: number;
  distance?: number;
  aiScore?: string[];
  preferenceScore?: number;
  postedDate?: number;
  enabled: boolean;
  header?: any;
  zipcode?: number;
  sort?: string[];
}

const formatUrlParams = (payload: any): URLSearchParams => {
  const params = new URLSearchParams();
  for (const props in payload) {
    try {
      payload[props].forEach((element: string) => {
        params.append(props, element);
      });
    } catch (e: any) {
      if (payload[props] as boolean) params.append(props, payload[props]);
      console.log("applied job payload might not an array", e.message);
    }
  }
  return params;
};

const GetJobs = async (
  payload: IGetJobsPayload
): Promise<AxiosPromise<any>> => {
  const { enabled, header, zipcode, postedDate, ...rest } = payload;
  // if (rest?.postedDate === "Newest") {
  //   rest.postedDate = "asc";
  // } else if (rest?.postedDate === "Oldest") rest.postedDate = "desc";

  const sectorIds = rest?.aiScore?.map((sector: string) => getSectorId(sector));

  const params = formatUrlParams({
    ...rest,
    days: postedDate,
    aiScore: sectorIds,
  });
  return await axios({
    method: "GET",
    url: "/jobs",
    params,
    headers: header,
  });
};

export const useGetJobsQuery = (
  payload: IGetJobsPayload
): UseQueryResult<AxiosPromise<any>, unknown> => {
  const toast = useToast();
  return useQuery([payload], async () => await GetJobs(payload), {
    onError: () => {
      ErrorToastHandler({
        toast,
        title: "Error",
        description: "Some error occurred when fetching jobs",
      });
    },
    enabled: payload.enabled,
    // keepPreviousData: true,
    cacheTime: 0,
  });
};

const GetJob = async (payload: any): Promise<AxiosPromise<any>> => {
  const { jobId } = payload;
  return await axios({
    method: "GET",
    url: `/jobs/${jobId as string}`,
  });
};

export const useGetJobQuery = (
  payload: any
): UseQueryResult<AxiosResponse<any, any>, unknown> => {
  return useQuery(
    [queryKeys.GET_JOB, payload],
    async () => await GetJob(payload),
    {
      enabled: payload?.enabled,
    }
  );
};

const GetScores = async (): Promise<AxiosPromise<any>> => {
  return await axios({
    url: "/score/me",
    method: "GET",
  });
};

export const useGetScoresQuery = (): UseQueryResult<
  AxiosPromise<any>,
  unknown
> => {
  return useQuery([queryKeys.GET_SCORES], GetScores, { enabled: true });
};

/**
 * @description like/dislike job apis
 * @param payload => object contains the jobId
 * @returns
 */
const likeJob = async (payload: any): Promise<AxiosPromise<any>> => {
  return await axios({
    method: "PUT",
    url: `/like/${payload?.jobId as string}`,
  });
};

export const useLikeJobQuery = (): UseMutationResult<
  AxiosPromise<any>,
  unknown,
  any,
  unknown
> => {
  const toast = useToast();
  return useMutation(likeJob, {
    onSuccess: () => {
      SuccessToastHandler({
        toast,
        title: "Success! 🎉",
        description: "Successfully liked the job",
      });
    },
    onError: () => {
      ErrorToastHandler({
        toast,
        title: "Error",
        description: "Some error occurred in like the job",
      });
    },
  });
};

const dislikeJob = async (payload: any): Promise<AxiosPromise<any>> => {
  return await axios({
    method: "DELETE",
    url: `/unlike/${payload?.jobId as string}`,
  });
};

export const useDislkeJobQuery = (): UseMutationResult<
  AxiosPromise<any>,
  unknown,
  any,
  unknown
> => {
  const toast = useToast();
  return useMutation(dislikeJob, {
    onSuccess: () => {
      SuccessToastHandler({
        toast,
        title: "Success! 🎉",
        description: "Successfully disliked the job",
      });
    },
    onError: () => {
      ErrorToastHandler({
        toast,
        title: "Error",
        description: "Some error occurred in dislike the job",
      });
    },
  });
};

/**
 * @description saved jobs apis
 */
interface ISaveJobs {
  jobId: string;
}
interface ITrackJobs {
  jobId: any;
  sectorId: string;
}

const getSavedJobs = async (
  payload: ISavedJobsPayload
): Promise<AxiosPromise<any>> => {
  const { enabled, header, postedDate, ...rest } = payload;

  const sectorIds = rest?.aiScore?.map((sector: string) => getSectorId(sector));

  const params = formatUrlParams({
    ...rest,
    days: postedDate,
    aiScore: sectorIds,
  });

  // if (rest?.postedDate === "Latest") {
  //   rest.postedDate = "asc";
  // } else if (rest?.postedDate === "Oldest") rest.postedDate = "desc";

  // const params = formatUrlParams({ ...rest, days: postedDate });
  return await axios({
    method: "GET",
    url: "/save-jobs",
    params,
    headers: header,
  });
};

export const useGetSavedJobsQuery = (
  payload: ISavedJobsPayload
): UseQueryResult<AxiosPromise<any>, unknown> => {
  return useQuery(
    [queryKeys.SAVED_JOBS, payload],
    async () => await getSavedJobs(payload),

    {
      enabled: payload.enabled,
      // keepPreviousData: true,
      // cacheTime: 0,
    }
  );
};

const saveJobs = async (payload: ISaveJobs): Promise<AxiosPromise<any>> => {
  return await axios({
    method: "POST",
    url: "/save-jobs",
    data: payload,
  });
};

const trackJobs = async (payload: ITrackJobs): Promise<AxiosPromise<any>> => {
  const payloadData = {
    jobId: payload?.jobId,
  };
  return await axios({
    method: "PUT",
    url: `job-click/${payload?.sectorId}`,
    data: payloadData,
  });
};

export const useTrackJobsQuery = (
  payload?: any
): UseMutationResult<AxiosPromise<any>, unknown, ITrackJobs, unknown> => {
  return useMutation(trackJobs, {});
};

export const useSaveJobsQuery = (
  payload?: any
): UseMutationResult<AxiosPromise<any>, unknown, ISaveJobs, unknown> => {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation(saveJobs, {
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.SAVED_JOBS],
      });
      SuccessToastHandler({
        toast,
        title: "Success! 🎉",
        description: payload?.message ?? "Successfully saved the job",
      });
    },
    onError: () => {
      ErrorToastHandler({
        toast,
        title: "Error",
        description: "Some error occurred while saving job",
      });
    },
  });
};

const unsaveJobs = async (payload: ISaveJobs): Promise<AxiosPromise<any>> => {
  return await axios({
    method: "DELETE",
    url: "/save-jobs",
    data: payload,
  });
};

export const useUnsaveJobQuery = (): UseMutationResult<
  AxiosPromise<any>,
  unknown,
  ISaveJobs,
  unknown
> => {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation(unsaveJobs, {
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.SAVED_JOBS],
      });
      SuccessToastHandler({
        toast,
        title: "Success! 🎉",
        description: "Successfully removed the job from saved job",
      });
    },
    onError: () => {
      ErrorToastHandler({
        toast,
        title: "Error",
        description: "Some error occurred while unsaving the job",
      });
    },
  });
};

/**
 * @description applided jobs queries
 */

const getAppliedJob = async (payload: any): Promise<AxiosPromise<any>> => {
  const params = formatUrlParams(payload);
  return await axios({
    method: "GET",
    url: "/jobs/apply",
    params,
  });
};

export const useGetApliedJobQuery = (
  payload: any
): UseQueryResult<AxiosResponse<any, any>, unknown> => {
  return useQuery(
    [queryKeys.APPLIED_JOBS, payload],
    async () => await getAppliedJob(payload),
    {
      enabled: true,
      // cacheTime: 0,
    }
  );
};

const applyJob = async (payload: any): Promise<AxiosPromise<any>> => {
  return await axios({
    method: "POST",
    url: "/jobs/apply",
    data: payload,
  });
};

export const useApplyJobQuery = (
  status: string,
  callback?: (arg?: any) => void
): UseMutationResult<unknown, unknown, void, unknown> => {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation(applyJob, {
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.APPLIED_JOBS],
      });
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.SAVED_JOBS],
      });
      status !== "Not interested" &&
        SuccessToastHandler({
          toast,
          title: "Success! 🎉",
          description:
            "Successfully saved the job as applied. You can view this job in the Applied jobs section",
        });
    },
    onError: () => {
      ErrorToastHandler({
        toast,
        title: "Error",
        description: "Some error occurred while saving the job as applied",
      });
      callback?.();
    },
  });
};

const updateAppliedJob = async (payload: any): Promise<AxiosPromise<any>> => {
  return await axios({
    method: "PATCH",
    url: "jobs/applied",
    data: payload,
  });
};

export const useUpdateAppliedJobQuery = (): UseMutationResult<
  AxiosPromise<any>,
  unknown,
  any,
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation(updateAppliedJob, {
    onSuccess: async () => {
      await queryClient.invalidateQueries([queryKeys.APPLIED_JOBS]);
    },
  });
};

/**
 * change zip code api  (at job search page filter)
 */

const updateZipCode = async (payload: any): Promise<AxiosPromise<any>> => {
  return await axios({
    method: "PATCH",
    url: "auth/update-zipcode",
    data: payload,
  });
};

export const useUpdateZipCodeQuery = (): UseMutationResult<
  AxiosPromise<any>,
  unknown,
  any,
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation(updateZipCode, {
    onSuccess: async () => {
      await queryClient.invalidateQueries([queryKeys.GET_USER_PROFILE]);
      const t = setTimeout(() => {
        void queryClient.invalidateQueries([queryKeys.GET_JOBS]);
        clearTimeout(t);
      }, 20000);
    },
  });
};

const updateVisited = async (): Promise<AxiosPromise<any>> => {
  return await axios({
    method: "PATCH",
    url: "auth/mark-visited",
  });
};

export const useUpdateVisited = (): UseMutationResult<
  AxiosPromise<any>,
  unknown,
  void,
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation(updateVisited, {
    onSuccess: async () => {
      await queryClient.invalidateQueries([queryKeys.GET_USER_PROFILE]);
    },
  });
};
